import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Tooltip from "@mui/material/Tooltip";
import FeedbackIcon from "@mui/icons-material/Feedback";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../store/store";
import { createCustomerPortalSession } from "../store/modules/subscription/subscriptionActions";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SettingsIcon from "@mui/icons-material/Settings";
import ShieldIcon from "@mui/icons-material/AdminPanelSettings";
import WelcomeName from "./WelcomeName";
import UserAccountMenu from "./UserAccountMenu";
import BarChartIcon from "@mui/icons-material/BarChart";
import MapIcon from "@mui/icons-material/Map";
import { RootState } from "../store/reducers";
import { useAffiliate } from "../affiliate/affiliateContext";
import { authRuntimeConfig } from "../authConfig";
import { getUser } from "../store/modules/user/userActions";

import ImportExportIcon from "@mui/icons-material/ImportExport";
import RepeatIcon from "@mui/icons-material/Repeat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LaunchIcon from "@mui/icons-material/Launch";
import TrafficIcon from "@mui/icons-material/Traffic";
import useMediaQuery from "@mui/material/useMediaQuery";
import Divider from "@mui/material/Divider";
import {
  getCommonData,
  getUserCustomData,
} from "../store/modules/common/commonActions";
import { fetchTransactionYears } from "../store/modules/transactionYear/transactionYearActions";
import {
  setHasHandledImpersonation,
  setHasHandledStopImpersonation,
} from "../store/modules/admin/adminStateActions";
import { getBrokerageAnniversaries } from "../store/modules/financialSettings/financialSettingsActions";
import { getHiddenFields } from "../store/modules/hiddenFields/hiddenFieldActions";
import { ToolName, isToolEnabled, useTools } from "../hooks/useTools";

const NavBar = ({
  children,
  title,
}: {
  children?: React.ReactNode;
  title?: string;
}) => {
  const theme = useTheme();
  const affiliate = useAffiliate();
  const productName = authRuntimeConfig.productName;
  // Taller on affiliate sites so the affiliate's logo, not the product name, leads. Everything that
  // sits below the fixed bar offsets by this same value.
  const appBarHeight = affiliate ? 80 : 64;
  const { currentUser: user } = useSelector((state: RootState) => state?.user);
  const { isImpersonating, impersonatedUserEmail, status } = useSelector(
    (state: RootState) => state.admin,
  );
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { hasHandledImpersonation, hasHandledStopImpersonation } = useSelector(
    (state: RootState) => state.admin,
  );
  const navigate = useNavigate();
  const drawerWidth = 130;

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  useEffect(() => {
    if (status === "Impersonated" && !hasHandledImpersonation) {
      dispatch(getUserCustomData());
      dispatch(fetchTransactionYears());
      dispatch(getCommonData());
      dispatch(getBrokerageAnniversaries());
      dispatch(getHiddenFields());
      dispatch(setHasHandledImpersonation());
      if (window.location.pathname !== "/") {
        navigate("/");
      }
    } else if (status === "Stopped" && !hasHandledStopImpersonation) {
      dispatch(getUserCustomData());
      dispatch(getUser());
      dispatch(getCommonData());
      dispatch(fetchTransactionYears());
      dispatch(getBrokerageAnniversaries());
      dispatch(getHiddenFields());

      dispatch(setHasHandledStopImpersonation());

      if (window.location.pathname !== "/") {
        navigate("/");
      }
    }
  }, [status]);

  const handleFeedbackClick = () => {
    const subject = encodeURIComponent(
      `${productName}- Feedback & Suggestions`,
    );
    const body = encodeURIComponent(`Hi,

I have feedback/suggestions for the ${productName} system:


Thank you!`);

    window.location.href = `mailto:support@agentsintel.com?subject=${subject}&body=${body}`;
  };

  const { data: tools } = useTools();

  const isUserSubscribed =
    user && user.subscriptionId && user.subscriptionId !== undefined
      ? true
      : false;
  // Tools turned off in Settings > Tools are left out of the menu
  const toolItems = [
    {
      tool: "searchMap",
      text: "Search & Map",
      icon: <MapIcon />,
      path: "/transactions/search-map",
    },
    {
      tool: "businessZones",
      text: "Business Zones",
      icon: <TrafficIcon />,
      path: "/business-zones",
    },
    {
      tool: "analytics",
      text: "Analytics",
      icon: <BarChartIcon />,
      path: "/analytics",
    },
    {
      tool: "repeatsReferrers",
      text: "Repeats & Referrers",
      icon: <RepeatIcon />,
      path: "/repeats-and-referrers",
    },
    {
      tool: "financialTrends",
      text: "Financial Trends",
      icon: <TrendingUpIcon />,
      path: "/financial-trends",
    },
    {
      tool: "businessPlanning",
      text: "Business Planning",
      icon: <AssignmentIcon />,
      path: "/business-planning",
    },
  ].filter((item) => isToolEnabled(tools, item.tool as ToolName));

  const navItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/" },
    ...toolItems.map(({ tool, ...item }) => item),

    {
      text: "Import/Export",
      icon: <ImportExportIcon />,
      path: "/import",
    },
    { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
    ...(user?.isAdmin === true
      ? [{ text: "Admin", icon: <ShieldIcon />, path: "/admin" }]
      : []),

    {
      text: "Subscription",
      icon: <ReceiptIcon />,
      onClick: !isUserSubscribed
        ? () => {
            dispatch(getUser());
          }
        : () => {
            if (user && user.subscriptionId) {
              dispatch(createCustomerPortalSession());
            }
          },
      path: !isUserSubscribed ? "/pricing" : null,
    },
    {
      text: "Resources",
      icon: <LaunchIcon />,
      path: null,
      onClick: () => {
        window.open("https://agentsintel.com/resources/", "_blank");
      },
    },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: (theme) =>
            `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
          borderBottom: "none",
        }}
      >
        {/* Explicit per breakpoint: MUI's own toolbar media queries would otherwise win on phones. */}
        <Toolbar
          sx={{
            minHeight: { xs: appBarHeight, sm: appBarHeight },
            px: { xs: 1, sm: 3 },
          }}
        >
          {isMobileOrTablet && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {!title && affiliate?.theme.logoUrl ? (
              // The affiliate's logo leads; the product credit sits beneath it, small, as it does
              // under the sign-in card.
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0.25,
                  minWidth: 0,
                  textDecoration: "none",
                  "&:hover": { opacity: 0.9 },
                }}
              >
                {/* The plate keeps a dark logo readable on the brand colour. */}
                <Box
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    display: "flex",
                  }}
                >
                  <Box
                    component="img"
                    src={affiliate.theme.logoUrl}
                    alt={affiliate.name}
                    sx={{
                      height: { xs: 36, sm: 48 },
                      maxWidth: { xs: 160, sm: 220 },
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    color: "rgba(255, 255, 255, 0.75)",
                    fontSize: "0.7rem",
                    lineHeight: 1.2,
                  }}
                >
                  Powered by {productName}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="h6"
                noWrap
                component={RouterLink}
                to="/"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  textDecoration: "none",
                  "&:hover": {
                    opacity: 0.9,
                  },
                }}
              >
                {title || affiliate?.name || productName}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <WelcomeName
              isImpersonating={isImpersonating}
              impersonatedUserEmail={impersonatedUserEmail}
            />
            <Tooltip title="Send Feedback & Suggestions" placement="bottom">
              <IconButton
                onClick={handleFeedbackClick}
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <FeedbackIcon />
              </IconButton>
            </Tooltip>
            <UserAccountMenu
              isImpersonating={isImpersonating}
              impersonatedUserEmail={impersonatedUserEmail}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer for navigation */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: "none",
          },
        }}
        variant={isMobileOrTablet ? "temporary" : "permanent"}
        anchor="left"
        open={isMobileOrTablet ? mobileDrawerOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(0, 1),
            minHeight: appBarHeight,
            justifyContent: "flex-end",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <IconButton size="small" sx={{ color: theme.palette.primary.main }}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </Box>
        <List sx={{ pt: 1 }}>
          {navItems
            //.filter((item) => !item.adminOnly || user?.isAdmin === true)
            .map((item, index) => (
              <React.Fragment key={item.text}>
                {item.text === "Resources" && <Divider sx={{ my: 1 }} />}
                <ListItem disablePadding>
                  <ListItemButton
                    component={item.path ? RouterLink : "div"}
                    to={item.path ?? "#"}
                    onClick={() => {
                      if (item.onClick) {
                        item.onClick();
                      }
                      if (isMobileOrTablet) {
                        setMobileDrawerOpen(false);
                      }
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "16px 8px",
                      gap: "8px",
                      textAlign: "center",
                      position: "relative",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(26, 54, 93, 0.08)",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "20%",
                          height: "60%",
                          width: 3,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: "0 4px 4px 0",
                        },
                      },
                      "& .MuiListItemIcon-root": {
                        minWidth: "auto",
                        color: theme.palette.primary.main,
                        opacity: 0.85,
                        transition: "all 0.2s",
                        margin: 0,
                      },
                      "& .MuiListItemText-root": {
                        margin: 0,
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: isMobileOrTablet ? 0 : `${drawerWidth}px`,
          width: isMobileOrTablet ? "100%" : `calc(100% - ${drawerWidth}px)`,
          maxWidth: isMobileOrTablet ? "100%" : `calc(100% - ${drawerWidth}px)`,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(0, 2),
            // Matches the fixed bar exactly, so no content starts underneath it.
            minHeight: appBarHeight,
          }}
        />
        <Box
          sx={{ padding: "24px", minHeight: `calc(100vh - ${appBarHeight}px)` }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
};

export default NavBar;
